/**
 * Disqualified Tenders page (Sprint 04-30 P1).
 * Mirrors the Dashboard's full-width card grid so the visual treatment is
 * identical to the search page. Each card has a Restore button instead of
 * Save/Disqualify.
 *
 * Bulk select: same UX as Dashboard's toolbar — toggle selection mode,
 * tick the cards (or click the body), then "Restore N" puts them back
 * into search.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, RotateCcw, Search, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { useDisqualifications } from '../contexts/DisqualificationContext';
import Pagination from '../components/Pagination';
import './Dashboard.css';

const Disqualified = () => {
  const {
    disqualifications, loading, error, undisqualify, refresh,
  } = useDisqualifications();
  const [filterText, setFilterText] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return disqualifications;
    return disqualifications.filter(
      (d) =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.organization || '').toLowerCase().includes(q) ||
        (d.tender_id || '').toLowerCase().includes(q) ||
        (d.user_name || '').toLowerCase().includes(q) ||
        (d.reason || '').toLowerCase().includes(q),
    );
  }, [disqualifications, filterText]);

  // Reset to page 1 when the filter or page size changes so the user
  // never lands on a phantom page (e.g. was on page 8 of 12, then
  // filter narrows to 3 pages — they'd be off the end).
  useEffect(() => { setCurrentPage(1); }, [filterText, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const clampedPage = Math.min(currentPage, totalPages);
  const paged = filtered.slice(
    (clampedPage - 1) * pageSize,
    clampedPage * pageSize,
  );

  const toggleSelected = (tid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(tid) ? next.delete(tid) : next.add(tid);
      return next;
    });
  };
  const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };

  const bulkRestore = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      for (const tid of selectedIds) {
        try { await undisqualify(tid); }
        catch (e) { console.warn('bulk restore (one) failed', tid, e); }
      }
    } finally {
      setBulkBusy(false);
      exitSelectionMode();
    }
  };

  const handleRestoreOne = async (tid) => {
    setBusyId(tid);
    try { await undisqualify(tid); } finally { setBusyId(null); }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      const norm = (typeof d === 'string' && d.length === 10) ? d + 'T00:00:00' : d;
      return format(new Date(norm), 'MMM do, yyyy');
    } catch { return d; }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-content-full">

          <div className="search-controls">
            <div className="dq-page-header">
              <div className="dq-page-title-row">
                <h1 className="dq-page-title">
                  <span className="dq-page-title-icon"><Ban size={14} /></span>
                  Disqualified
                </h1>
                <span className="dq-page-subtitle">
                  Hidden from the search dashboard until restored
                </span>
              </div>
              {disqualifications.length > 0 && (
                <div className="dq-page-count">
                  {disqualifications.length} flagged
                </div>
              )}
            </div>

            <form className="search-bar" onSubmit={(e) => e.preventDefault()}>
              <Search className="search-icon" />
              <input
                type="search"
                placeholder="Filter by title, organization, tender id, person, or reason…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </form>
          </div>

          {/* Bulk-select toolbar — mirror of Dashboard */}
          <div className="bulk-toolbar">
            {!selectionMode ? (
              <button
                type="button"
                className="bulk-toolbar-btn"
                onClick={() => setSelectionMode(true)}
                disabled={loading || filtered.length === 0}
              >
                ☑ Select tenders
              </button>
            ) : (
              <>
                <span className="bulk-toolbar-count">{selectedIds.size} selected</span>
                <button
                  type="button"
                  className="bulk-toolbar-btn primary-green"
                  onClick={bulkRestore}
                  disabled={selectedIds.size === 0 || bulkBusy}
                >
                  <RotateCcw size={12} /> {bulkBusy ? 'Restoring…' : `Restore ${selectedIds.size}`}
                </button>
                <button
                  type="button"
                  className="bulk-toolbar-btn"
                  onClick={() => setSelectedIds(new Set(filtered.map((d) => d.tender_id)))}
                  disabled={bulkBusy}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="bulk-toolbar-btn"
                  onClick={exitSelectionMode}
                  disabled={bulkBusy}
                >
                  Cancel
                </button>
              </>
            )}
            <div className="filter-chip" title="Results per page" style={{ marginLeft: 'auto' }}>
              <FileText size={14} />
              <select
                aria-label="Results per page"
                className="filter-chip-select"
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              >
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
            <button
              type="button"
              className="bulk-toolbar-btn"
              onClick={refresh}
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          {loading && <div className="loading">Loading disqualifications…</div>}
          {error === 'unauthorized' && <div className="loading">Please sign in to view disqualified tenders.</div>}
          {error && error !== 'unauthorized' && <div className="loading">Failed to load disqualifications.</div>}

          {!loading && !error && filtered.length === 0 && (
            <div className="loading">
              No disqualified tenders.{' '}
              Use the <Ban size={14} style={{ verticalAlign: 'middle' }} /> button on any tender to disqualify it.
            </div>
          )}

          <div className="tenders-list">
            {paged.map((d) => {
              const tid = d.tender_id;
              const isSelected = selectedIds.has(tid);
              return (
                <div
                  key={d.id || tid}
                  className={`tender-card-wrapper ${selectionMode ? 'selection-mode' : ''} ${isSelected ? 'selected' : ''}`}
                >
                  {selectionMode && (
                    <input
                      type="checkbox"
                      className="bulk-select-checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelected(tid)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <Link
                    to={selectionMode ? '#' : `/tender/${encodeURIComponent(tid)}`}
                    className="tender-card"
                    onClick={(e) => {
                      if (selectionMode) {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSelected(tid);
                      }
                    }}
                  >
                    <div className="tender-card-header">
                      <div className="header-left">
                        <span className="tender-status status-disqualified">
                          Disqualified
                        </span>
                        <h3 className="tender-title">{d.title || tid}</h3>
                      </div>
                      <div className="header-actions">
                        <button
                          className="action-btn save-btn active"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRestoreOne(tid);
                          }}
                          disabled={busyId === tid}
                          title="Restore to search"
                        >
                          <RotateCcw size={12} />
                          {busyId === tid ? 'Restoring…' : 'Restore'}
                        </button>
                      </div>
                    </div>

                    <div className="tender-highlights">
                      {d.closing_date && (
                        <p className="highlight-field">
                          <strong>Closing Date:</strong> {fmtDate(d.closing_date)}
                        </p>
                      )}
                      <p className="highlight-field">
                        <strong>Disqualified:</strong> {fmtDate(d.disqualifiedAt || d.disqualified_at)}
                      </p>
                    </div>

                    <div className="tender-details-grid">
                      <div className="tender-left-column">
                        <p>
                          <strong>Disqualified by:</strong> {d.user_name || d.user_id}
                        </p>
                        {d.reason && (
                          <p>
                            <strong>Reason:</strong> {d.reason}
                          </p>
                        )}
                      </div>
                      <div className="tender-right-column">
                        <p>
                          <strong>Organization:</strong> {d.organization || '—'}
                        </p>
                        <p>
                          <strong>Reference:</strong>{' '}
                          <span className="reference-number">{tid}</span>
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>

          {filtered.length > pageSize && (
            <Pagination
              currentPage={clampedPage}
              totalItems={filtered.length}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              itemLabel="disqualified tender"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Disqualified;
